import NextLink from 'next/link';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MUIBreadcrumbs from '@mui/material/Breadcrumbs';
import Paper from '@mui/material/Paper';

export const SITE_MASTER_COLORS = {
  pageBg: '#F7F8FA',
  teal: '#255E5E',
  tealLight: '#4AA585',
  accentSurface: '#D6ECE4',
  textPrimary: '#28333A',
  textSecondary: '#3F4040',
  textDark: '#0F172A',
  chipNeutralBg: '#EEF0F2',
  chipPopulationBg: '#F2F2F2',
  border: '#E9E9E9',
  closed: '#E7262C',
} as const;

export const StSiteMasterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: 0,
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
}));

export const StSiteMasterSectionTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2.5),
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 700,
  color: SITE_MASTER_COLORS.teal,
  fontFamily: theme.typography.fontFamily,
  letterSpacing: 0,
}));

export const StSiteMasterSubTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(1.5),
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const StSiteMasterLabel = styled('dt')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

export const StSiteMasterValue = styled('dd')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
}));

export const StSiteMasterDetailGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  background: SITE_MASTER_COLORS.pageBg,
  margin: '0 auto',
  padding: '24px',
  [theme.breakpoints.up('md')]: {
    padding: '32px 40px',
    maxWidth: 1366,
    margin: '0 auto',
  },
}));

export const StSiteMasterDetailWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 888px) minmax(0, 374px)',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: 1286,
  margin: '0 auto',
}));

export const StSiteMasterDetailColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  width: '100%',
}));

export const StSiteMasterSidebarColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
}));

export const StSiteMasterStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const StSiteMasterChipList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  listStyle: 'none',
  margin: 0,
  padding: 0,
}));

export const StSiteMasterChip = styled('li')(({ theme }) => ({
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.spacing(2.5),
  backgroundColor: theme.palette.grey[100],
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
}));

export const StSiteMasterGlanceSection = styled(StSiteMasterSection)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  '& h2': {
    marginBottom: 0,
  },
  [theme.breakpoints.up('lg')]: {
    // Figma sidebar card is 374px; no theme spacing token
    width: 374,
    maxWidth: '100%',
  },
}));

export const StSiteMasterStatGrid = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(3),
  listStyle: 'none',
  margin: 0,
  padding: 0,
  [theme.breakpoints.up('lg')]: {
    // Figma 2x2 cells are 110px with 46px column gap; no theme spacing token
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 110px)',
    columnGap: '46px',
    rowGap: theme.spacing(2),
  },
}));

export const StSiteMasterStat = styled('li')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  textAlign: 'left',
});

export const StSiteMasterStatValue = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(28),
  fontWeight: 700,
  color: SITE_MASTER_COLORS.teal,
  lineHeight: 1,
}));

export const StSiteMasterStatLabel = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  color: SITE_MASTER_COLORS.textSecondary,
  lineHeight: 1.3,
}));

export const StSiteMasterHeroContainer = styled(Box)({});
export const StSiteMasterHeroWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  color: theme.palette.common.white,
  padding: '24px 20px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.up('md')]: {
    padding: '32px 32px 48px',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 1366,
    margin: '0 auto',
    padding: '76px 96px 40px',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
  },
}));

export const StSiteMasterHeroLeft = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const StSiteMasterHeroDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  '& .site-hero-title': {
    margin: 0,
    fontFamily: `${theme.palette.secondaryFontFamilyType}, 'Noto Serif', serif`,
  },
}));

const BREADCRUMB_MUTED = 'rgba(255, 255, 255, 0.8)';

export const StSiteMasterBreadcrumb = styled(MUIBreadcrumbs)({
  alignSelf: 'stretch',
  '& .MuiBreadcrumbs-ol': {
    alignItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  '& .MuiBreadcrumbs-li': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiBreadcrumbs-separator': {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    color: BREADCRUMB_MUTED,
    '& svg': {
      width: 16,
      height: 16,
    },
  },
});

export const StSiteMasterBreadcrumbLink = styled(NextLink)({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: BREADCRUMB_MUTED,
  textDecoration: 'none',
  '&:hover, &:focus-visible': {
    textDecoration: 'underline',
  },
});

export const StSiteMasterBreadcrumbText = styled('span')({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: BREADCRUMB_MUTED,
});

export const StSiteMasterBreadcrumbCurrent = styled('span')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 700,
  color: theme.palette.common.white,
}));

export const StSiteMasterHeroTagList = styled('ul')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  listStyle: 'none',
  margin: 0,
  padding: 0,
});

export const StSiteMasterHeroImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '273px',
  borderRadius: '24px',
  overflow: 'hidden',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  [theme.breakpoints.up('lg')]: {
    height: '100%',
    minHeight: 273,
    width: 375,
  },
}));

export const StSiteMasterStudyCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: '20px',
  border: `1px solid ${SITE_MASTER_COLORS.border}`,
  borderRadius: '12px',
  backgroundColor: theme.palette.common.white,
  '& + &': {
    marginTop: 0,
  },
}));

export const StSiteMasterStudyCardHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'reserveStatusSpace',
})<{ reserveStatusSpace?: boolean }>(({ theme, reserveStatusSpace }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  // Desktop: reserve space so absolute Enrolling does not overlap chips
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    paddingRight: reserveStatusSpace ? '100px' : 0,
    gap: 0,
    '& > ul': {
      width: '100%',
    },
  },
}));

export const StSiteMasterStudyCardTags = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  alignItems: 'center',
});

export const StSiteMasterStudyCardStatus = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  // Mobile: sits under conditions / phase in document flow
  // Desktop: pinned top-right
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingTop: '2px',
  },
  '& .status-dot': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: SITE_MASTER_COLORS.tealLight,
    flexShrink: 0,
  },

  '& .status-label, & .status-label *': {
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: `${theme.palette.primaryFontSemiBoldType || theme.typography.fontFamily}`,
    color: SITE_MASTER_COLORS.tealLight,
    whiteSpace: 'nowrap',
  },
}));

export const StSiteMasterStudyCardActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  width: '100%',
}));

export const StSiteMasterExploreBanner = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: 0,
  borderRadius: '12px',
  boxShadow: 'none',
  backgroundColor: SITE_MASTER_COLORS.accentSurface,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

export const StSiteMasterWhyChooseSection = styled(Box)(({ theme }) => ({
  // Same outer box as SiteDetail grid so this jss-main sibling stays centered
  width: '100%',
  maxWidth: 1366,
  margin: '0 auto',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
  backgroundColor: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: '32px 40px',
    gap: theme.spacing(6),
  },
}));

export const StSiteMasterWhyChooseHeading = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  '& h2': {
    margin: 0,
    fontFamily: `${theme.palette.secondaryFontFamilyType}, 'Noto Serif', serif`,
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 700,
    color: SITE_MASTER_COLORS.teal,
    lineHeight: 'normal',
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },
  },

  '& .underline-bar': {
    width: 80,
    height: 4,
    borderRadius: 2,
    backgroundColor: SITE_MASTER_COLORS.teal,
  },
}));

export const StSiteMasterStaffList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  listStyle: 'none',
  margin: 0,
  padding: 0,
}));

export const StSiteMasterStaffProfile = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  border: `1px solid ${SITE_MASTER_COLORS.border}`,
  borderRadius: '12px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
  backgroundColor: theme.palette.common.white,
}));

export const StSiteMasterStaffAvatar = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: SITE_MASTER_COLORS.teal,
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.pxToRem(18),
  fontWeight: 700,
  flexShrink: 0,
}));

export const StSiteMasterStaffCopy = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const StSiteMasterStaffName = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 700,
  lineHeight: 'normal',
  color: SITE_MASTER_COLORS.textPrimary,
}));

export const StSiteMasterStaffMeta = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.4,
  color: SITE_MASTER_COLORS.textSecondary,
}));

export const StSiteMasterAdvantagesContainer = styled(Box)(({ theme }) => ({
  backgroundColor: SITE_MASTER_COLORS.pageBg,
  borderRadius: '16px',
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },

  '& .advantages-heading': {
    margin: 0,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: SITE_MASTER_COLORS.teal,
    textTransform: 'uppercase',
  },

  '& .advantages-body': {
    '& p': {
      margin: 0,
      marginBottom: theme.spacing(1),
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 400,
      lineHeight: 1.5,
      color: SITE_MASTER_COLORS.textSecondary,
    },
    '& ul': { margin: 0, paddingLeft: theme.spacing(2.5) },
    '& li': {
      marginBottom: theme.spacing(1),
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 400,
      lineHeight: 1.5,
      color: SITE_MASTER_COLORS.textSecondary,
    },
    '& li:last-child': { marginBottom: 0 },
    '& p:last-child': { marginBottom: 0 },
  },
}));

export const StSiteMasterNearbySection = styled(Box)(({ theme }) => ({
  backgroundColor: SITE_MASTER_COLORS.pageBg,
  padding: theme.spacing(3, 4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },

  '& .nearby-heading': {
    margin: 0,
    fontFamily: `${theme.palette.secondaryFontFamilyType}, 'Noto Serif', serif`,
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 400,
    color: SITE_MASTER_COLORS.teal,
    lineHeight: 'normal',
  },
}));

export const StSiteMasterNearbyGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

export const StSiteMasterNearbyCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: '12px',
  backgroundColor: theme.palette.common.white,
  height: '100%',

  '& .site-name': {
    margin: 0,
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    color: SITE_MASTER_COLORS.textDark,
    lineHeight: 'normal',
  },

  '& .location-row': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
    color: SITE_MASTER_COLORS.textSecondary,

    '& .MuiSvgIcon-root': {
      fontSize: 16,
      color: SITE_MASTER_COLORS.textSecondary,
    },
  },
}));
